import { apiFetch, ApiError } from './api';

/**
 * Recomendador de plantillas (pantalla de entrada por problema).
 *
 * Contrato C7 / decisión congelada: en F0-P3/P4 la IA NO está cableada todavía
 * (el driver claude_cli espera el binario en el contenedor — F0-P5). Por eso el
 * backend devuelve, POR AHORA, 2 plantillas FIJAS con su porqué.
 *
 * TODO F0-P5: cuando /ai esté disponible, el backend derivará la recomendación
 * del enunciado real vía el driver de IA; el frontend ya consume este contrato.
 */

export interface PlantillaRecomendada {
  id: string;
  nombre: string;
  /** El "porqué" que alimenta la barra XIA: por qué esta plantilla, para qué. */
  porque: string;
  /** Resumen del recorrido de fases de la plantilla. */
  fases: string[];
  /** Etiqueta de sector/uso sugerido. */
  etiqueta: string;
}

export interface RecomendacionRespuesta {
  recomendadas: PlantillaRecomendada[];
}

/** Sectores de dominio público GT para el selector del problema. */
export const SECTORES: { value: string; label: string }[] = [
  { value: 'agua', label: 'Agua y saneamiento' },
  { value: 'movilidad', label: 'Movilidad y vialidad' },
  { value: 'seguridad', label: 'Seguridad ciudadana' },
  { value: 'justicia', label: 'Justicia' },
  { value: 'ambiente', label: 'Ambiente y residuos' },
  { value: 'educacion', label: 'Educación' },
  { value: 'salud', label: 'Salud' },
  { value: 'economia', label: 'Economía local' },
  { value: 'empresa', label: 'Empresa privada' },
  { value: 'otro', label: 'Otro' },
];

/**
 * TIPO de solución que se busca — dimensión ORTOGONAL al sector: una solución
 * digital puede ser del sector Justicia («¿qué pasa si es software del sector
 * Justicia?» — el owner). El backend ya separa sector y tipo (tabla problemas
 * y GET /plantillas/recomendar).
 */
export const TIPOS_SOLUCION: { value: string; label: string }[] = [
  { value: 'servicio', label: 'Servicio o proceso público' },
  { value: 'digital', label: 'Solución digital / software' },
  { value: 'requerimientos', label: 'Levantar requerimientos de software' },
  { value: 'politica', label: 'Política o normativa' },
  { value: 'otro', label: 'Otro' },
];

/**
 * Fallback local de 2 plantillas fijas, idéntico en espíritu a lo que el
 * backend devuelve. Se usa si el endpoint aún no está disponible en este
 * entorno, para que la pantalla siempre enseñe algo útil.
 */
const FALLBACK: PlantillaRecomendada[] = [
  {
    id: 'diagnostico-expres-2-sesiones',
    nombre: 'Diagnóstico exprés 2 sesiones',
    porque:
      'Tu problema todavía está abierto: conviene primero reunir las voces y evidenciar el problema real antes de saltar a soluciones.',
    fases: ['Abrir el problema', 'Priorizar causas'],
    etiqueta: 'Para problemas amplios o poco definidos',
  },
  {
    id: 'sprint-8-medios-dias',
    nombre: 'Sprint 8 medios días',
    porque:
      'Si el problema ya está claro, el sprint corto lleva del diagnóstico a una propuesta trazable con responsables y primeros pasos.',
    fases: ['Empatizar', 'Definir', 'Idear', 'Prototipar', 'Testear'],
    etiqueta: 'Para problemas concretos que piden propuesta pronto',
  },
];

/** Forma real del backend (api/v1/plantillas.py): slug + XIA. */
interface RecomendacionBackend {
  recomendaciones?: {
    slug: string;
    nombre: string;
    duracion: string;
    sesiones: number;
    ideal_para: string;
    xia: { que: string; por_que: string; hacia: string };
  }[];
}

/**
 * Pide al backend las plantillas recomendadas para un enunciado + sector.
 * Ante 404/501 (endpoint aún no montado) cae al fallback fijo local.
 */
export async function recomendarPlantillas(
  enunciado: string,
  sector: string,
  tipo?: string,
): Promise<PlantillaRecomendada[]> {
  const params = new URLSearchParams({ enunciado, sector });
  if (tipo) params.set('tipo', tipo);
  try {
    const data = await apiFetch<RecomendacionRespuesta & RecomendacionBackend>(
      `/plantillas/recomendar?${params.toString()}`,
    );
    // Contrato real del backend: `recomendaciones` con slug + textos XIA. El
    // slug ES la plantilla instanciable (POST /procesos {plantilla: slug}).
    if (data?.recomendaciones?.length) {
      return data.recomendaciones.map((r) => ({
        id: r.slug,
        nombre: r.nombre,
        porque: r.xia.por_que,
        fases: [`${r.duracion}`, `${r.sesiones} sesiones`],
        etiqueta: r.ideal_para,
      }));
    }
    if (data?.recomendadas?.length) return data.recomendadas;
    return FALLBACK;
  } catch (err) {
    // Endpoint aún no implementado en este entorno: usamos el fijo local.
    if (err instanceof ApiError && (err.status === 404 || err.status === 501 || err.status === 0)) {
      return FALLBACK;
    }
    throw err;
  }
}
